/**
 * Schemas describing ReploAgent session shape: the session "type" enum that
 * routes prompts on the coordinator, plus the per-session metadata that the
 * agent-ui keeps in its store so it can render type-specific UI (titles,
 * completion rows, etc.) without re-deriving from RPC args.
 *
 * @author Kevin OConnell 2026-04-21
 */
import { z } from "zod";

import { uiMessagePartsSchema } from "./mentionable";
import { selectModeSourceLocationSchema } from "./selectMode";
import { siteReportTypeSchema } from "./siteReport";

// The ordered pipeline stages of a publish, shared by the harness, coordinator, and debugger; lives here (not agentConnection) because the publish session metadata embeds it and agentConnection already imports this module.
export const publishPhaseSchema = z
  .enum(["build", "upload", "publisher", "tag"])
  .describe("PublishPhase");

export const opencodeSessionTypeSchema = z
  .enum([
    "main",
    "subagent",
    "selectModeInlineEdit",
    "publish",
    "gitConflict",
    "duplicatePage",
    "deletePage",
    "analyticsScriptDetection",
    "filesystemSession",
    "scheduledTask",
    "memoryCuration",
    "tutorialPersonalization",
    "siteReport",
  ])
  .describe("OpencodeSessionType");

const opencodeMiniappSourceMetadataSchema = z.object({
  id: z.string(),
  reuseIdentifier: z.string().optional(),
});

const opencodeAgentUiSourceMetadataSchema = z.object({
  reuseIdentifier: z.string().optional(),
});

const opencodeSlackSourceMetadataSchema = z.object({
  slackWorkspaceId: z.string(),
  slackChannelId: z.string(),
  slackThreadId: z.string(),
});

const opencodeTaskSourceMetadataSchema = z.object({
  taskRunId: z.string(),
});

const opencodeAgentUiSourceContextSchema = z.object({
  source: z.literal("agent-ui"),
  sourceMetadata: opencodeAgentUiSourceMetadataSchema,
});

const opencodeSlackSourceContextSchema = z.object({
  source: z.literal("slack"),
  sourceMetadata: opencodeSlackSourceMetadataSchema,
});

const opencodeMiniappSourceContextSchema = z.object({
  source: z.literal("miniapp"),
  sourceMetadata: opencodeMiniappSourceMetadataSchema,
});

const opencodeTaskSourceContextSchema = z.object({
  source: z.literal("task"),
  sourceMetadata: opencodeTaskSourceMetadataSchema,
});

// Shared by the public REST API and the public MCP server: both create sessions
// through bedrock's public request path, differing only in the source label.
const opencodePublicRequestSourceMetadataSchema = z.object({
  requestId: z.string(),
});

const opencodePublicApiSourceContextSchema = z.object({
  source: z.literal("public-api"),
  sourceMetadata: opencodePublicRequestSourceMetadataSchema,
});

const opencodePublicMcpSourceContextSchema = z.object({
  source: z.literal("public-mcp"),
  sourceMetadata: opencodePublicRequestSourceMetadataSchema,
});

export const opencodeSourceContextSchema = z
  .discriminatedUnion("source", [
    opencodeAgentUiSourceContextSchema,
    opencodeSlackSourceContextSchema,
    opencodeMiniappSourceContextSchema,
    opencodeTaskSourceContextSchema,
    opencodePublicApiSourceContextSchema,
    opencodePublicMcpSourceContextSchema,
  ])
  .describe("OpencodeSourceContext");

// NOTE (Kevin O'Connell, 2026-04-22, REPL-26014): Per-element snapshot projected
// into `source-element` chips; geometry-only fields are excluded to save bytes.
export const selectModeEditElementSnapshotSchema = z
  .object({
    kind: z.literal("react-element"),
    tagName: z.string(),
    elementType: z.string(),
    textContent: z.string().nullable(),
    sourceLocation: selectModeSourceLocationSchema.optional(),
  })
  .describe("SelectModeEditElementSnapshot");

export const selectModeEditPlanSnapshotSchema = z
  .object({
    kind: z.literal("plan"),
    planId: z.string(),
    label: z.string(),
  })
  .describe("SelectModeEditPlanSnapshot");

export const selectModeEditSelectableSnapshotSchema = z
  .discriminatedUnion("kind", [
    selectModeEditElementSnapshotSchema,
    selectModeEditPlanSnapshotSchema,
  ])
  .describe("SelectModeEditSelectableSnapshot");

// NOTE (Kevin O'Connell, 2026-04-23, REPL-26014): Variants mirror
// `selectModeSelectionTypeSchema` in ./selectMode so the metadata
// vocabulary stays in lockstep with the runtime one. The `drag` variant
// is structurally representable but unreachable in normal flow —
// `SelectModeInput` hides the submit panel while a drag is in progress.
// We carry it anyway so `projectSelectModeSelectionToMetadata` can stay
// a total `exhaustiveSwitch` over `SelectModeSelection`.
export const selectModeEditSelectionMetadataSchema = z
  .discriminatedUnion("type", [
    z.object({ type: z.literal("none") }),
    z.object({
      type: z.literal("element"),
      data: selectModeEditSelectableSnapshotSchema,
    }),
    z.object({
      type: z.literal("area"),
      items: z.array(selectModeEditSelectableSnapshotSchema),
    }),
    z.object({ type: z.literal("drag") }),
  ])
  .describe("SelectModeEditSelectionMetadata");

// NOTE (Kevin, 2026-04-21): Single source of truth for side-session
// variants. `sideSessionMetadataSchema` exposes these directly;
// `sessionMetadataSchema` adds the non-side-session branches (`main`,
// `subagent`) on top. Adding a new side session means editing this tuple
// only — both unions stay in sync automatically.
const sideSessionMetadataOptions = [
  z.object({
    type: z.literal("selectModeInlineEdit"),
    pageRoute: z.string(),
    currentSandboxFilePath: z.string().nullable(),
    // Ties the session back to the canopy optimistic override applied at commit
    // time so it can be cleared once this edit finishes streaming. Optional so
    // sessions started before this field existed still validate.
    editId: z.string().optional(),
  }),
  z.object({ type: z.literal("deletePage"), route: z.string() }),
  z.object({ type: z.literal("duplicatePage"), route: z.string() }),
  z.object({
    type: z.literal("publish"),
    // The site this publish targets, set by the client when the publish starts.
    // Read back off the coordinator's metadata broadcast to label the popover
    // ("Publishing <name>") and survive a reload.
    siteId: z.uuid(),
    // Coordinator-written, write-once. "succeeded" the moment a publish tool
    // part completes (the live site is updated then); "failed" if the session
    // concludes without one. Absent while the publish is in flight.
    outcome: z.enum(["succeeded", "failed"]).optional(),
    // (Cole, 2026-07-09, REPL-27972): coordinator-written display state — the in-flight publish's current pipeline phase; overwritten as phases advance and never read for control flow.
    phase: publishPhaseSchema.optional(),
    content: z
      .object({
        route: z.string().startsWith("/"),
        operation: z.enum(["publish", "unpublish", "archive"]),
      })
      .optional(),
  }),
  z.object({ type: z.literal("gitConflict") }),
  z.object({ type: z.literal("memoryCuration") }),
  z.object({ type: z.literal("tutorialPersonalization") }),
  z.object({
    type: z.literal("analyticsScriptDetection"),
    providerKey: z.string(),
    configValue: z.string(),
    action: z.enum(["add", "remove"]),
  }),
] as const;

export const sideSessionMetadataSchema = z
  .discriminatedUnion("type", sideSessionMetadataOptions)
  .describe("SideSessionMetadata");

// The prompt every `publish` session is started with, whether the trigger is
// the editor Publish button or the public `publish_site` tool. The coordinator
// auto-approves the publish permission for this session type, so the prompt
// must not leave the agent room to pause on the "which site" question.
export function buildPublishPrompt({ siteId }: { siteId: string }): string {
  return (
    `Publish the site with siteId ${siteId} now to make all changes live. Publish that ` +
    "site — do not ask which site, and do not go looking for its folder on disk; the " +
    "publish tool takes only the siteId. Follow the publish skill exactly as " +
    "written. Do NOT ask the user any questions about the publishing process — this " +
    "was triggered by the user explicitly requesting a publish, so proceed without " +
    "confirmation and do not pause to ask whether " +
    "to commit or whether to retry. First run the publish tool. If publish fails due to " +
    "build/runtime issues, use Website debugger to query runtime errors and logs, fix " +
    "actionable issues, and retry publish."
  );
}

// (Noah, 2026-07-11, REPL-29203): Marks a main-chat session that a miniapp
// kicked off to generate a brand kit, so Brand Studio can re-derive its
// "generating" state from session state (surviving a page refresh) instead of a
// local flag. Carried inside the main session's `meta` bag; also reused by the
// sendMessageToMainSession bridge input.
export const brandGenerationSessionMetadataSchema = z.object({
  brandSlug: z.string(),
});

// (Noah, 2026-07-11, REPL-29203): An open-ended bag of app-specific markers on a
// main session, kept separate from the session's identity so new markers can be
// added without touching the discriminated union.
export const mainSessionMetaSchema = z.object({
  brandGeneration: brandGenerationSessionMetadataSchema.optional(),
});

export const sessionMetadataSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("main"),
      meta: mainSessionMetaSchema.optional(),
    }),
    z.object({
      type: z.literal("subagent"),
      parentToolCallId: z.string().nullable(),
    }),
    z.object({ type: z.literal("filesystemSession") }),
    z.object({ type: z.literal("scheduledTask") }),
    z.object({
      type: z.literal("siteReport"),
      siteId: z.uuid(),
      reportType: siteReportTypeSchema,
      reportId: z.uuid(),
    }),
    ...sideSessionMetadataOptions,
  ])
  .describe("SessionMetadata");

// A composer message the user queued while the agent was busy. `parts` are the
// already-resolved wire parts (durable file urls/ids), i.e. exactly what a send
// would transmit — so a persisted queued message is self-contained and needs no
// attachment reconstruction on restore.
export const persistedQueuedMessageSchema = z
  .object({
    id: z.string(),
    prompt: z.string(),
    parts: uiMessagePartsSchema,
  })
  .describe("PersistedQueuedMessage");
